import { useQuery } from '@tanstack/react-query';
import { USE_CASE_NAMES } from '@whistler-ui/k2/constants/websheetsConstants';
import { formatDateAsYMD } from '@whistler-ui/k2/helpers';
import { renameKeys } from '@whistler-ui/k2/helpers/common';
import { sortChronologically } from '@whistler-ui/k2/helpers/sortHelpers';
import {
  fetchAnalyticsData,
  getBillingBreakdown,
  getBillingData,
  getRCSReports,
  getReportsOverview,
  getReportsUseCase,
} from '@whistler-ui/portal/services/reporting';

import { REPORTS_ANALYTICS_TYPES } from '../../constants';
import { calculateWatchActivationTotal } from '../../helpers/billing';
import { useEntitlementsStore } from '../../store/entitlements';
import { Filter, useReportsStore } from '../../store/reports/useReportsStore';
import { useThemeStore } from '../../store/theme';
import {
  AnalyticsType,
  BillingDetailsType,
  DeviceTypeApiResponse,
  EsimActivationApiResponse,
  FailedAttemptApiResponse,
  FunnelDataApiResponse,
  PerUserApiResponse,
  QualityOfServiceApiResponse,
  RCSDailyBreakdown,
  RCSReportParams,
  RCSReportResponse,
  StepByStepPerformanceStatsResponse,
  TopLevelStatisticsApiResponse,
  TUseCaseTag,
  TypeOfTransferApiResponse,
} from '../../types';

interface AnalyticsQuerySettings<TData, TransformedData> {
  analyticsType?: AnalyticsType;
  transformer?: (
    data: TData,
    colors?: Record<string, string>,
  ) => TransformedData;
  enabled?: boolean;
}

export const useGetReportsOverview = (
  environmentId: string | undefined,
  from: Date,
  to: Date,
  platform = 'IOS',
) => {
  return useQuery({
    queryKey: ['get_reports_overview', environmentId, from, to, platform],
    queryFn: () => getReportsOverview(environmentId, from, to, platform),
    enabled: Boolean(environmentId),
    retry: false,
  });
};

export const useGetReportsUseCase = ({
  environmentId,
  useCaseId,
  from,
  to,
}: {
  environmentId: string | undefined;
  useCaseId: TUseCaseTag;
  from: string;
  to: string;
}) => {
  return useQuery({
    queryKey: ['get_reports_use_case', environmentId, useCaseId, from, to],
    queryFn: () => getReportsUseCase(environmentId, useCaseId, from, to),
    enabled: Boolean(environmentId),
  });
};

export const useEsimActivationChart = <
  TData = EsimActivationApiResponse,
  TransformedData = TData,
>(
  settings: Omit<
    AnalyticsQuerySettings<TData, TransformedData>,
    'analyticsType'
  > = {},
) => {
  const { transformer, enabled = true } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['eSimActivationChart', filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'activation-mechanism',
        environmentId: environment.id,
        filters,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
};

// export const useWatchActivationChart = <TData = WatchActivationApiResponse>(
//   select?: (data: WatchActivationApiResponse) => TData,
// ): UseQueryResult<TData, Error> => {
export const useWatchActivationChart = <
  TData = unknown,
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const { transformer, enabled = true } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['watchActivationChart', filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'activation-mechanism',
        environmentId: environment.id,
        filters,
        analyticsType: REPORTS_ANALYTICS_TYPES.WATCH,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
};

export const useGetRCSReport = (
  environmentId: string,
  filters: Filter,
  enabled = true,
) => {
  const params: RCSReportParams = filters.days
    ? { days: filters.days }
    : {
        start_date: formatDateAsYMD(filters.startDate) || undefined,
        end_date: formatDateAsYMD(filters.endDate) || undefined,
      };

  return useQuery<RCSReportResponse>({
    queryKey: ['rcs_report', environmentId, filters],
    queryFn: () => getRCSReports(environmentId, params),
    enabled: !!environmentId && enabled,
    retry: false,
  });
};

export const useGetTopLevelStats = <
  TData = TopLevelStatisticsApiResponse,
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  const isRCS = analyticsType === REPORTS_ANALYTICS_TYPES.RCS;
  const rcsReport = useGetRCSReport(environment.id, filters, isRCS && enabled);

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['top_level_statistics', analyticsType, filters, environment.id],
    queryFn: async () => {
      if (isRCS) {
        if (rcsReport.isError) {
          throw rcsReport.error;
        }

        const { date_range, ...summary } = rcsReport.data?.summary ?? {};
        return summary as TData;
      }

      return fetchAnalyticsData<TData>({
        endpoint: 'top-level-statistics',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    select: transformer,
    retry: false,
    enabled: isRCS
      ? enabled && (!!rcsReport.data || rcsReport.isError)
      : enabled,
  });
};

export const useGetUserTypeBreakdownStats = <
  TData = unknown,
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['userType', analyticsType, filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'user-type',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
};

export const useGetDeviceTypeStats = <
  TData = DeviceTypeApiResponse,
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['deviceType', analyticsType, filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'device-type',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
};

export const useGetTypeOfTransferStats = <
  TData = TypeOfTransferApiResponse,
  TransformedData = TData,
>(
  settings: Omit<
    AnalyticsQuerySettings<TData, TransformedData>,
    'analyticsType'
  > = {},
) => {
  const { transformer, enabled = true } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['transferType', filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'transfer-type',
        environmentId: environment.id,
        filters,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
};

export const useGetWatchPerUserStats = <
  TData = PerUserApiResponse,
  TransformedData = TData,
>(
  settings: Omit<
    AnalyticsQuerySettings<TData, TransformedData>,
    'analyticsType'
  > = {},
) => {
  const { transformer } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['perUser', filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'per-user',
        environmentId: environment.id,
        filters,
        analyticsType: REPORTS_ANALYTICS_TYPES.WATCH,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
  });
};

export const useGetStepByStepPerformance = <
  TData = StepByStepPerformanceStatsResponse,
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;
  const { current: environment } = useEntitlementsStore();

  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery({
    queryKey: [
      'get_step_by_step_performance',
      analyticsType,
      environment.id,
      filters,
    ],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'step-by-step-performance',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    enabled,
    select: transformer ? data => transformer(data) : undefined,
    retry: false,
  });
};

export const useRCSDailyBreakdown = <
  TData = RCSDailyBreakdown[],
  TransformedData = TData,
>(
  settings: AnalyticsQuerySettings<TData, TransformedData> = {},
) => {
  const { transformer, enabled = true } = settings;
  const { current: environment } = useEntitlementsStore();

  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  const rcsReport = useGetRCSReport(environment.id, filters);

  return useQuery<TData, Error, TransformedData>({
    queryKey: ['rcs_daily_breakdown', environment.id, filters],
    queryFn: async () => {
      if (rcsReport.isError) {
        throw rcsReport.error;
      }

      return (rcsReport.data?.daily_breakdown ?? []) as unknown as TData;
    },
    enabled: enabled && (!!rcsReport.data || rcsReport.isError),
    select: transformer ? data => transformer(data) : undefined,
    retry: false,
  });
};

export function useGetFunnelChartStats<
  TData = FunnelDataApiResponse,
  TransformedData = TData,
>(settings: AnalyticsQuerySettings<TData, TransformedData> = {}) {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;

  const { current: environment } = useEntitlementsStore();

  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery({
    queryKey: ['funnelChartStats', analyticsType, environment.id, filters],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'success-funnel',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    retry: false,
    select: transformer ? data => transformer(data) : undefined,
    enabled,
  });
}

export function useGetQualityGraphStats<
  TData = QualityOfServiceApiResponse,
  TransformedData = TData,
>(settings: AnalyticsQuerySettings<TData, TransformedData> = {}) {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;
  const { current: environment } = useEntitlementsStore();

  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery({
    queryKey: ['qualityGraphStats', analyticsType, environment.id, filters],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'service-quality',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    select: transformer ? data => transformer(data) : undefined,
    enabled: enabled,
    retry: false,
  });
}

export function useGetFailedAttemptsStats<
  TData = FailedAttemptApiResponse,
  TransformedData = TData,
>(settings: AnalyticsQuerySettings<TData, TransformedData> = {}) {
  const {
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER,
    transformer,
    enabled = true,
  } = settings;

  const { current: environment } = useEntitlementsStore();
  const { getCurrentFilter } = useReportsStore();
  const filters = getCurrentFilter();
  const { colors } = useThemeStore();

  if (!environment?.id) {
    throw new Error('Environment ID is missing');
  }

  return useQuery({
    queryKey: ['failedAttempts', analyticsType, filters, environment.id],
    queryFn: async () => {
      return fetchAnalyticsData<TData>({
        endpoint: 'failed-attempts',
        environmentId: environment.id,
        filters,
        analyticsType,
      });
    },
    select: transformer ? data => transformer(data, colors) : undefined,
    retry: false,
    enabled,
  });
}

export const useGetBilling = ({
  month,
  version = 'v1',
}: {
  month: string;
  version?: 'v1' | 'v2';
}) => {
  type MonthItem = { month: string; [key: string]: unknown };
  interface DataObject {
    items: Record<string, MonthItem[]>;
    [key: string]: unknown;
  }

  interface QueryResult {
    transfer_rate: number;
    items: BillingDetailsType;
  }

  const { current: environment } = useEntitlementsStore();

  const sortAllItems = (data: DataObject) => {
    return {
      ...data,
      items: Object.fromEntries(
        Object.entries(data.items).map(([key, arr]) => [
          key,
          sortChronologically(arr),
        ]),
      ),
    };
  };

  const envId = environment?.id;

  return useQuery<QueryResult, Error>({
    queryKey: ['billing_data', envId, month],
    queryFn: async () => {
      const [billingDataResult, billingBreakdownResult] =
        await Promise.allSettled([
          getBillingData(envId, month),
          getBillingBreakdown(envId, month),
        ]);

      const billingData =
        billingDataResult.status === 'fulfilled'
          ? billingDataResult.value
          : null;

      const billingBreakdown =
        billingBreakdownResult.status === 'fulfilled'
          ? billingBreakdownResult.value
          : null;

      const result = {
        transfer_rate: billingData?.transfer_rate ?? 0,
        items: {
          ...billingData?.items,
          [USE_CASE_NAMES.WATCH]: billingBreakdown?.items
            ? calculateWatchActivationTotal(billingBreakdown.items)
            : undefined,
        },
      };

      return result;
    },
    retry: false,
    enabled: Boolean(envId),
    select: data => {
      if (version === 'v1') {
        return data;
      }

      if (version === 'v2') {
        const testData = {
          ...data,
          items: renameKeys(data.items, {
            eSIM_quick_transfer: USE_CASE_NAMES.DEVICE_TRANSFER,
          }),
        };

        return sortAllItems(testData) as QueryResult;
      }
    },
  });
};
